import { ObjectSchema } from "@hapi/joi";

import Joi, {
  getAttachmentsSchema,
  scaleTicketSchema,
} from "fe-shared/src/helpers/validation";
import { ErrorMessages, FileInterface } from "fe-shared/src/types/common";
import {
  MaterialCategoryName,
  MaterialGroup,
  MaterialUnit,
  EndMarketRole,
} from "fe-shared/src/types/domain";

const { RPM, Reuse, Landfill, EnergyFuel, Recycling } = EndMarketRole;
const { Tires, Products } = MaterialCategoryName;

export const minDate = new Date("2020-01-01T00:00:00");

export const MATERIAL_GROUPS_BY_END_MARKET_ROLE_CODE: {
  [key in EndMarketRole]: MaterialCategoryName[];
} = {
  [RPM]: [Products],
  [Reuse]: [Tires],
  [Landfill]: [Tires, Products],
  [EnergyFuel]: [Products],
  [Recycling]: [Products],
};

export interface MaterialFormContent {
  materialCode: string;
  units: {
    unitQuantity: number;
    unit: MaterialUnit;
  };
  maxQuantity: number;
  availableQuantity: number;
}

type Content = { [key: string]: unknown };

export interface EditDispositionFormContent extends Content {
  endMarketRoleCode: EndMarketRole;
  dispositionCompanyName: string;
  materialGroups: MaterialGroup<MaterialFormContent>[];
  attachments: FileInterface[];
  scaleTicketDate: string | null;
  scaleTicketNumber: string;
  dispositionInvoiceDate: string | null;
  dispositionInvoiceNumber: string;
}

export const initialContent: EditDispositionFormContent = {
  endMarketRoleCode: EndMarketRole.RPM,
  dispositionCompanyName: "",
  materialGroups: [],
  attachments: [],
  scaleTicketDate: null,
  scaleTicketNumber: "",
  dispositionInvoiceDate: null,
  dispositionInvoiceNumber: "",
};

export const errorMessages: ErrorMessages = {
  "materialGroups.items.units.unitQuantity": {
    "number.base": "validation.materialUnitQuantityNumber",
    "number.max": "validation.materialMaxQuantity",
  },
  dispositionCompanyName: {
    "string.empty": "validation.dispositionCompanyNameRequired",
  },
  attachments: {
    "array.min": "validation.documentsRequired",
  },
  scaleTicketDate: {
    "string.empty": "validation.scaleTicketDateRequired",
  },
  scaleTicketNumber: {
    "string.empty": "validation.scaleTicketNumberRequired",
    "string.pattern.name": "validation.scaleTicketNumberInvalid",
  },
  dispositionInvoiceDate: {
    "string.empty": "validation.dispositionInvoiceDateRequired",
  },
  dispositionInvoiceNumber: {
    "string.empty": "validation.dispositionInvoiceNumberRequired",
    "string.pattern.name": "validation.dispositionInvoiceNumberInvalid",
  },
};

export const validationConfig: ObjectSchema<EditDispositionFormContent> = Joi.object(
  {
    endMarketRoleCode: Joi.allow(...Object.values(EndMarketRole)).required(),
    dispositionCompanyName: Joi.string().required(),
    materialGroups: Joi.array()
      .min(1)
      .items(
        Joi.object({
          unit: Joi.string().valid(...Object.values(MaterialUnit)),
          materialCategoryName: Joi.allow(
            ...Object.values(MaterialCategoryName)
          ),
          items: Joi.array()
            .min(1)
            .items(
              Joi.object({
                materialCode: Joi.string().required(),
                units: Joi.object({
                    unitQuantity: Joi.number()
                      .min(0)
                      .when(
                        Joi.ref("/endMarketRoleCode", {
                          adjust: (v: EndMarketRole) =>
                            MATERIAL_GROUPS_BY_END_MARKET_ROLE_CODE[v],
                        }),
                        {
                          is: Joi.array().has(
                            Joi.ref("......materialCategoryName")
                          ),
                          then: Joi.number().max(Joi.ref("...maxQuantity")),
                        }
                      ),
                  unit: Joi.string(),
                }),
                  maxQuantity: Joi.number().required(),
                  availableQuantity: Joi.number(),
              })
            ),
        })
      ),
    attachments: getAttachmentsSchema("attachments").min(1),
    scaleTicketDate: Joi.string().required(),
    scaleTicketNumber: scaleTicketSchema.required(),
    dispositionInvoiceDate: Joi.string().required(),
    dispositionInvoiceNumber: scaleTicketSchema.required(),
  }
);
